import type { QueryFilter } from '@/types';

/**
 * Type guard to check if a value is an array
 */
const isArray = (value: unknown): value is unknown[] => Array.isArray(value);

/**
 * Checks if an operator is a multi-value operator that requires double-wrapping
 * @param operator - The UI operator string (before translation to API format)
 * @returns true if the operator requires multi-value handling
 */
export const isMultiValueOperator = (operator: string): boolean => {
  const multiValueOperators = ['is any of', 'is none of'];
  return multiValueOperators.includes(operator);
};

/**
 * Packs a simple array into a double-wrapped array for the API
 * Converts UI format [a, b, c] to API format [[a, b, c]]
 *
 * @param value - The value from SimpleFilter (string or string[])
 * @param converter - Function to convert string values to the target type (e.g., parseInt, String)
 * @param operator - The UI operator to determine if wrapping is needed
 * @returns The packed value ready for API (single value, array, or double-wrapped array)
 *
 * @example
 * // For numbers
 * packMultiValueForAPI(['80', '443'], v => parseInt(v, 10), 'is any of')
 * // Returns: [[80, 443]]
 *
 * @example
 * // For strings
 * packMultiValueForAPI(['web', 'api'], v => v, 'is any of')
 * // Returns: [['web', 'api']]
 *
 * @example
 * // Single value
 * packMultiValueForAPI('80', v => parseInt(v, 10), 'is')
 * // Returns: 80
 */
export const packMultiValueForAPI = <T>(
  value: string | string[],
  converter: (value: string) => T,
  operator: string
): T | T[] | T[][] => {
  const isMultiValue = isMultiValueOperator(operator);

  if (isMultiValue && isArray(value)) {
    // Multi-value operator with array: double-wrap
    const convertedValues = value.map(converter);
    return [convertedValues];
  } else if (isArray(value)) {
    // Array but not multi-value operator: single-wrap
    return value.map(converter);
  } else {
    // Single value: no wrapping
    return converter(value);
  }
};

/**
 * Unpacks a double-wrapped array from the API into a simple array for the UI
 * Converts API format [[a, b, c]] to UI format ['a', 'b', 'c']
 *
 * @param value - The value from QueryFilter (can be any type including nested arrays)
 * @param converter - Function to convert API values to strings for UI
 * @param operator - The UI operator (after translation) to determine if unpacking is needed
 * @returns The unpacked value ready for UI (string or string[])
 *
 * @example
 * // For numbers
 * unpackMultiValueForUI([[80, 443, 8080]], v => String(v), 'is any of')
 * // Returns: ['80', '443', '8080']
 *
 * @example
 * // For strings
 * unpackMultiValueForUI([['web', 'api']], v => v, 'is any of')
 * // Returns: ['web', 'api']
 *
 * @example
 * // Single value
 * unpackMultiValueForUI(80, v => String(v), 'is')
 * // Returns: '80'
 *
 * @example
 * // Fallback: single-wrapped array (not double-wrapped)
 * unpackMultiValueForUI([80, 443], v => String(v), 'is any of')
 * // Returns: ['80', '443']
 */
export const unpackMultiValueForUI = <T>(
  value: QueryFilter['value'],
  converter: (value: T) => string,
  operator: string
): string | string[] => {
  const isMultiValue = isMultiValueOperator(operator);

  if (!value && isMultiValue) {
    return [];
  } else if (!value) {
    return '';
  }

  if (isMultiValue && isArray(value)) {
    // Multi-value operator with array
    const firstElement = value[0];

    if (isArray(firstElement)) {
      // Double-wrapped array: unwrap outer array
      return (firstElement as T[]).map(converter);
    } else {
      // Single-wrapped array (fallback): convert directly
      return (value as T[]).map(converter);
    }
  } else if (isArray(value)) {
    // Array but not multi-value operator
    return (value as T[]).map(converter);
  } else {
    // Single value
    return converter(value as T);
  }
};

/**
 * Convenience function for number-based filters (like Port Number)
 * Packs string values to integers with double-wrapping for multi-value operators
 */
export const packNumbersForAPI = (
  value: string | string[],
  operator: string
): number | number[] | number[][] => {
  return packMultiValueForAPI(value, v => parseInt(v, 10), operator);
};

/**
 * Convenience function for number-based filters (like Port Number)
 * Unpacks integer values to strings with unwrapping for multi-value operators
 */
export const unpackNumbersForUI = (
  value: QueryFilter['value'],
  operator: string
): string | string[] => {
  return unpackMultiValueForUI<number>(value, v => String(v), operator);
};

/**
 * Convenience function for string-based filters
 * Packs string values with double-wrapping for multi-value operators
 */
export const packStringsForAPI = (
  value: string | string[],
  operator: string
): string | string[] | string[][] => {
  return packMultiValueForAPI(value, v => v, operator);
};

/**
 * Convenience function for string-based filters
 * Unpacks string values with unwrapping for multi-value operators
 */
export const unpackStringsForUI = (
  value: QueryFilter['value'],
  operator: string
): string | string[] => {
  return unpackMultiValueForUI<string>(value, v => v, operator);
};
